/**
 * OVATION auroral oval, the fetch and refresh half of loader-aurora.js: the
 * SWPC nowcast ([lon, lat, prob] cells) is rendered to a PNG data URL by the
 * globe module (renderAuroraDataUrl) and pushed through engine.setAuroraImage
 * by the runtime. Refreshes every 5 minutes while enabled; null when disabled.
 */
import { renderAuroraDataUrl } from '../globe/auroraImage';
import { getOvation, type OvationResponse } from './feeds';
import { useAwareness } from './store';
import { errorMessage, startPolling } from './util';

export const AURORA_REFRESH_MS = 5 * 60 * 1000;

/** The cell array under either key SWPC has used; null when absent or empty. */
export function ovationCoordinates(data: OvationResponse | null | undefined): number[][] | null {
  const cells = data?.coordinates ?? data?.Coordinates ?? null;
  if (!Array.isArray(cells) || cells.length === 0) return null;
  return cells;
}

export async function refreshAurora(render: (cells: number[][]) => string = renderAuroraDataUrl): Promise<void> {
  const store = useAwareness.getState();
  try {
    const data = await getOvation();
    const cells = ovationCoordinates(data);
    if (!cells) throw new Error('OVATION response had no coordinates array');
    store.setAurora(render(cells), null);
  } catch (err) {
    console.warn('[aurora] OVATION feed unavailable:', err);
    useAwareness.getState().setAurora(useAwareness.getState().auroraDataUrl, errorMessage(err));
  }
}

/** Refresh now and every 5 minutes until stopped; the image is cleared on stop. */
export function startAurora(): () => void {
  const stop = startPolling(() => {
    void refreshAurora();
  }, AURORA_REFRESH_MS);
  return () => {
    stop();
    useAwareness.getState().setAurora(null, null);
  };
}
